/**
 * Tipos para el módulo Pagos — Fase 5
 *
 * Documentos relacionados:
 *   - requerimientos.md § M10 (Pagos y Cobranza)
 *   - DECISIONES.md #006 (cobranza manual), #023 (modelo de pagos Fase 5)
 *
 * Reglas clave:
 *   - Solo operador / admin_general registran pagos (RLS). La empresa NO inserta
 *     en `pagos`: informa su transferencia vía RPC `informar_pago_empresa`, que
 *     crea un pago pendiente con comprobante (ADR #043).
 *   - Pagos parciales permitidos: el cupón se cierra cuando
 *     SUM(monto_pagado WHERE estado='acreditado') >= cupon.monto_total.
 *   - Comprobante obligatorio para ventanilla_banco y transferencia_otra_cuenta
 *     al pasar a estado 'acreditado' (validado por trigger SQL).
 */

// ── Enums alineados con el schema SQL ─────────────────────────────────────────

export type PagoVia =
  | 'cvu_pronto_pago'
  | 'ventanilla_banco'
  | 'transferencia_otra_cuenta'
  | 'remesa_csv_legacy';

export type PagoEstado = 'pendiente' | 'pre_confirmado' | 'acreditado' | 'rechazado';

// ── Helpers de presentación ───────────────────────────────────────────────────

export function getEstadoPagoLabel(estado: PagoEstado | string): string {
  const labels: Record<string, string> = {
    pendiente:      'Pendiente',
    pre_confirmado: 'Pre-confirmado',
    acreditado:     'Acreditado',
    rechazado:      'Rechazado',
  };
  return labels[estado] ?? estado;
}

export function getEstadoPagoColor(
  estado: PagoEstado | string
): 'default' | 'warning' | 'info' | 'success' | 'error' {
  switch (estado) {
    case 'pendiente':      return 'warning';
    case 'pre_confirmado': return 'info';
    case 'acreditado':     return 'success';
    case 'rechazado':      return 'error';
    default:               return 'default';
  }
}

export function getViaPagoLabel(via: PagoVia | string): string {
  const labels: Record<string, string> = {
    cvu_pronto_pago:           'CVU (Pronto Pago)',
    ventanilla_banco:          'Ventanilla / Banco',
    transferencia_otra_cuenta: 'Transferencia',
    remesa_csv_legacy:         'Remesa CSV (legacy)',
  };
  return labels[via] ?? via;
}

/** Vías para las que el comprobante es obligatorio al acreditar.
 *  Para `cvu_pronto_pago` la obligatoriedad depende del origen:
 *    - manual (sin transaccion_recaudacion_id): sí, exige (ver decisión #028)
 *    - vía webhook (con transaccion vinculada): no, la evidencia es la transacción
 *  El trigger SQL hace el check final; en UI siempre exigimos para CVU manual. */
export const VIAS_COMPROBANTE_OBLIGATORIO: PagoVia[] = [
  'ventanilla_banco',
  'transferencia_otra_cuenta',
  'cvu_pronto_pago',
];

export function requiereComprobante(via: PagoVia | string): boolean {
  return VIAS_COMPROBANTE_OBLIGATORIO.includes(via as PagoVia);
}

/** Vías que el operador puede elegir al registrar un pago manual.
 *  - cvu_pronto_pago (manual): caso de webhook atrasado o ventana del PULL
 *    expirada, con comprobante del payer (decisión #028).
 *  - remesa_csv_legacy: se carga por batch al migrar (Fase 9). */
export const VIAS_MANUALES: PagoVia[] = [
  'ventanilla_banco',
  'transferencia_otra_cuenta',
  'cvu_pronto_pago',
];

// ── Entidad ───────────────────────────────────────────────────────────────────

export interface Pago {
  id: string;
  empresa_id: string;
  periodo_id: string;
  cupon_id: string | null;
  planilla_id: string | null;
  via_pago: PagoVia;
  monto_total: number;
  monto_pagado: number;
  /** Generada: monto_pagado - monto_total. Positiva = pagó de más. */
  diferencia: number;
  fecha_pago: string;
  fecha_acreditacion: string | null;
  estado: PagoEstado;
  medio_pago: string | null;
  nro_comprobante: string | null;
  comprobante_url: string | null;
  observaciones: string | null;
  confirmado_por: string | null;
  fecha_confirmacion: string | null;
  rechazado_por: string | null;
  fecha_rechazo: string | null;
  motivo_rechazo: string | null;
  transaccion_recaudacion_id: string | null;
  /** true = lo informó la propia empresa con comprobante; pendiente de verificación por ATSA (ADR #043). */
  informado_por_empresa?: boolean;
  created_at: string;
  updated_at: string;
  // Joins opcionales (cuando se piden expandidos)
  empresa?: { id: string; razon_social: string; cuit: string } | null;
  periodo?: { id: string; periodo: string; descripcion: string | null } | null;
  cupon?: {
    id: string;
    codigo: string;
    monto_total: number;
    estado: string;
  } | null;
}

// ── Regla de pagos definitivos (ADR #044) ─────────────────────────────────────

/**
 * Un pago es DEFINITIVO si ya hay plata confirmada: está 'acreditado' o ya fue
 * conciliado con una transacción de Pronto Pago (y no está rechazado). Un pago
 * definitivo impide eliminar o reabrir la DDJJ de su cupón.
 */
export function esPagoDefinitivo(
  p: Pick<Pago, 'estado' | 'transaccion_recaudacion_id'>,
): boolean {
  if (p.estado === 'rechazado') return false;
  return p.estado === 'acreditado' || !!p.transaccion_recaudacion_id;
}

/**
 * Pago NO definitivo (pendiente / pre_confirmado, sin transacción de Pronto Pago):
 * al eliminar o reabrir la DDJJ se rechaza solo, con motivo.
 */
export function esPagoNoDefinitivo(
  p: Pick<Pago, 'estado' | 'transaccion_recaudacion_id'>,
): boolean {
  return (
    (p.estado === 'pendiente' || p.estado === 'pre_confirmado') &&
    !p.transaccion_recaudacion_id
  );
}

// ── Payloads ──────────────────────────────────────────────────────────────────

/** Datos requeridos para registrar un pago manual (estado siempre 'pendiente'). */
export interface PagoRegistrarPayload {
  empresa_id: string;
  periodo_id: string;
  cupon_id: string | null;
  planilla_id?: string | null;
  via_pago: PagoVia;
  monto_total: number;            // monto del cupón al momento de registrar
  monto_pagado: number;           // lo que efectivamente se paga
  fecha_pago: string;             // ISO date
  medio_pago?: string | null;
  nro_comprobante?: string | null;
  comprobante_url?: string | null;
  observaciones?: string | null;
}

/** Datos que informa la empresa al avisar una transferencia (ADR #043). */
export interface PagoInformarEmpresaPayload {
  cupon_id: string;
  empresa_id: string;
  monto_pagado: number;
  fecha_pago: string;             // ISO date
  nro_comprobante: string;
  medio_pago: string;
  observaciones?: string | null;
  file: File;                     // comprobante (obligatorio)
}

export interface PagoConfirmarPayload {
  id: string;
  userId: string;                 // confirmado_por (auth.uid del cliente)
}

export interface PagoRechazarPayload {
  id: string;
  userId: string;                 // rechazado_por
  motivo: string;
}
